import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  adminListPosts,
  adminUpsertPost,
  adminDeletePost,
  type BlogPost,
} from "@/lib/blog.functions";

export const Route = createFileRoute("/_authenticated/admin/blog")({
  head: () => ({
    meta: [
      { title: "Admin — Blog beheer" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminBlogPage,
});

type FormState = {
  id?: string;
  slug: string;
  title: string;
  excerpt: string;
  cover_url: string;
  content: string;
  author_name: string;
  is_published: boolean;
};

const EMPTY: FormState = {
  slug: "",
  title: "",
  excerpt: "",
  cover_url: "",
  content: "",
  author_name: "",
  is_published: false,
};

function slugify(s: string) {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120);
}

function AdminBlogPage() {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const listFn = useServerFn(adminListPosts);
  const upsertFn = useServerFn(adminUpsertPost);
  const deleteFn = useServerFn(adminDeletePost);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "blog"],
    queryFn: () => listFn(),
    enabled: isAdmin,
  });

  const [form, setForm] = useState<FormState>(EMPTY);

  const upsert = useMutation({
    mutationFn: (input: FormState) =>
      upsertFn({
        data: {
          id: input.id,
          slug: input.slug,
          title: input.title,
          excerpt: input.excerpt,
          cover_url: input.cover_url || undefined,
          content: input.content,
          author_name: input.author_name || undefined,
          is_published: input.is_published,
        },
      }),
    onSuccess: () => {
      toast.success("Artikel opgeslagen");
      setForm(EMPTY);
      qc.invalidateQueries({ queryKey: ["admin", "blog"] });
      qc.invalidateQueries({ queryKey: ["blog"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Opslaan mislukt"),
  });

  const del = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Verwijderd");
      qc.invalidateQueries({ queryKey: ["admin", "blog"] });
      qc.invalidateQueries({ queryKey: ["blog"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Verwijderen mislukt"),
  });

  function edit(p: BlogPost) {
    setForm({
      id: p.id,
      slug: p.slug,
      title: p.title,
      excerpt: p.excerpt ?? "",
      cover_url: p.cover_url ?? "",
      content: p.content ?? "",
      author_name: p.author_name ?? "",
      is_published: p.is_published,
    });
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }

  if (!isAdmin) {
    return <p className="text-text-secondary">Geen toegang.</p>;
  }

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-bold">Blog beheer</h1>
        <p className="text-sm text-text-secondary">
          Maak, bewerk en publiceer blogartikelen.
        </p>
      </header>

      <section className="rounded-xl border bg-card p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-bold">
          {form.id ? "Artikel bewerken" : "Nieuw artikel"}
        </h2>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label htmlFor="title">Titel</Label>
            <Input
              id="title"
              value={form.title}
              onChange={(e) => {
                const t = e.target.value;
                setForm((f) => ({
                  ...f,
                  title: t,
                  slug: f.id ? f.slug : slugify(t),
                }));
              }}
              placeholder="Bijv. 5 tips voor een veilige groepenkast"
            />
          </div>

          <div>
            <Label htmlFor="slug">Slug (URL)</Label>
            <Input
              id="slug"
              value={form.slug}
              onChange={(e) =>
                setForm((f) => ({ ...f, slug: slugify(e.target.value) }))
              }
              placeholder="bijv-5-tips-veilige-groepenkast"
            />
            <p className="mt-1 text-xs text-text-secondary">
              /blog/{form.slug || "..."}
            </p>
          </div>

          <div>
            <Label htmlFor="author">Auteur (optioneel)</Label>
            <Input
              id="author"
              value={form.author_name}
              onChange={(e) =>
                setForm((f) => ({ ...f, author_name: e.target.value }))
              }
              placeholder="Bijv. Team Elvion"
            />
          </div>

          <div className="sm:col-span-2">
            <Label htmlFor="cover">Cover-afbeelding URL (optioneel)</Label>
            <Input
              id="cover"
              value={form.cover_url}
              onChange={(e) =>
                setForm((f) => ({ ...f, cover_url: e.target.value }))
              }
              placeholder="https://..."
            />
          </div>

          <div className="sm:col-span-2">
            <Label htmlFor="excerpt">Samenvatting</Label>
            <Textarea
              id="excerpt"
              rows={2}
              value={form.excerpt}
              onChange={(e) =>
                setForm((f) => ({ ...f, excerpt: e.target.value }))
              }
              placeholder="Korte inleiding, zichtbaar op overzicht en in zoekresultaten."
            />
          </div>

          <div className="sm:col-span-2">
            <Label htmlFor="content">Inhoud</Label>
            <Textarea
              id="content"
              rows={14}
              value={form.content}
              onChange={(e) =>
                setForm((f) => ({ ...f, content: e.target.value }))
              }
              placeholder="Schrijf hier het volledige artikel. Witregels worden behouden."
            />
          </div>

          <div className="sm:col-span-2 flex items-center gap-2">
            <input
              id="pub"
              type="checkbox"
              checked={form.is_published}
              onChange={(e) =>
                setForm((f) => ({ ...f, is_published: e.target.checked }))
              }
              className="size-4"
            />
            <Label htmlFor="pub" className="cursor-pointer">
              Publiceren (zichtbaar op /blog)
            </Label>
          </div>
        </div>

        <div className="mt-5 flex gap-3">
          <Button
            onClick={() => upsert.mutate(form)}
            disabled={upsert.isPending || !form.title || !form.slug}
          >
            {form.id ? "Wijzigingen opslaan" : "Artikel aanmaken"}
          </Button>
          {form.id && (
            <Button variant="outline" onClick={() => setForm(EMPTY)}>
              Annuleren
            </Button>
          )}
        </div>
      </section>

      <section className="rounded-xl border bg-card p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-bold">Alle artikelen</h2>
        {isLoading ? (
          <p className="text-sm text-text-secondary">Laden…</p>
        ) : !data?.posts?.length ? (
          <p className="text-sm text-text-secondary">Nog geen artikelen.</p>
        ) : (
          <ul className="divide-y">
            {data.posts.map((p) => (
              <li
                key={p.id}
                className="flex flex-wrap items-center justify-between gap-3 py-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{p.title}</span>
                    <span
                      className={
                        "rounded px-2 py-0.5 text-xs font-bold " +
                        (p.is_published
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-700")
                      }
                    >
                      {p.is_published ? "Gepubliceerd" : "Concept"}
                    </span>
                  </div>
                  <div className="text-xs text-text-secondary">/blog/{p.slug}</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => edit(p)}>
                    Bewerken
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      if (confirm(`Verwijder "${p.title}"?`)) del.mutate(p.id);
                    }}
                  >
                    Verwijderen
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
